"use client";

import Link from "next/link";
import { Phone } from "lucide-react";
import { cn } from "@/lib/utils";
import { trackEvent } from "@/lib/analytics";
import { formatUsPhone, type PhoneNumber } from "@/lib/constants/contact";

export interface HeroCallLine {
  /** Names the business unit the line reaches, e.g. "Collision Shop". */
  label: string;
  /** A whole CONTACT.phones entry: href, data-phone and display come from it. */
  phone: PhoneNumber;
}

interface HeroActionsProps {
  bookLabel: string;
  bookHref: string;
  /** Caption above the call lines, e.g. "Call us". */
  callLabel: string;
  /** One line per business unit, in the order the contact page lists them. */
  calls: readonly HeroCallLine[];
  locale: string;
  className?: string;
}

const CALL_LABEL_ID = "hero-call-label";

/**
 * The hero's action block: one filled button, then every line a visitor can
 * call.
 *
 * Exactly one control is filled. The call lines are outlined pills, so the
 * booking button is unambiguously the primary action even beside two offer
 * cards — hierarchy rather than subtraction.
 *
 * The site has three business units on three numbers, and the contact page
 * lists all of them. A hero that shows only the mechanic line sends collision
 * and parts callers to the wrong desk, or to the contact page to find the
 * right one. Each pill takes its href, `data-phone` and visible number from a
 * single `PhoneNumber` object, so a display string can never leak into a
 * `tel:` (see lib/constants/contact.ts).
 *
 * Below `sm` the pills stack full-width with the unit on the left and the
 * number on the right: three 44px rows a thumb can hit, rather than three
 * cramped chips. From `sm` they flow inline and wrap.
 */
export function HeroActions({
  bookLabel,
  bookHref,
  callLabel,
  calls,
  locale,
  className,
}: HeroActionsProps) {
  return (
    <div className={cn("flex flex-col gap-5", className)}>
      <Link
        href={bookHref}
        // The hero's visual hierarchy depends on exactly one filled action.
        // This marker — not the `bg-yellow-500` class — is what the covering
        // test counts, so the invariant survives a restyle to a different
        // yellow token instead of silently breaking.
        data-hero-primary=""
        onClick={() => trackEvent({ event: "hero_cta_click", locale })}
        className={cn(
          "inline-flex min-h-[56px] w-full items-center justify-center rounded-full border border-transparent bg-yellow-500 px-7 py-4 text-base font-medium text-black transition-colors duration-200 hover:bg-yellow-400 motion-reduce:transition-none",
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-yellow-500",
          "sm:w-auto sm:self-start",
        )}
      >
        {bookLabel}
      </Link>

      <div>
        <p
          id={CALL_LABEL_ID}
          // Sentence case on purpose: the eyebrow above the headline is the
          // hero's one tracked-caps kicker, and repeating that grammar here
          // would turn a brand mark into section scaffolding.
          className="mb-2.5 inline-flex items-center gap-2 text-[13px] font-medium text-white/60"
        >
          <Phone className="h-4 w-4 text-yellow-500" aria-hidden="true" />
          {callLabel}
        </p>
        <ul
          aria-labelledby={CALL_LABEL_ID}
          className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-2"
        >
          {calls.map(({ label, phone }) => {
            const number = formatUsPhone(phone.display);
            return (
              <li key={phone.key}>
                <a
                  href={phone.telHref}
                  data-phone={phone.key}
                  aria-label={`${label}: ${number}`}
                  onClick={() =>
                    trackEvent({
                      event: "hero_call_click",
                      locale,
                      line: phone.key,
                    })
                  }
                  className={cn(
                    "group flex min-h-[44px] items-center justify-between gap-3 rounded-full border border-white/15 bg-white/[0.03] px-3.5 py-2 text-[13px] transition-colors duration-200 motion-reduce:transition-none",
                    "hover:border-yellow-500/60 hover:bg-yellow-500/[0.06]",
                    "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-yellow-500",
                    "sm:justify-start sm:gap-2",
                  )}
                >
                  <span className="text-white/55 transition-colors group-hover:text-white/80 motion-reduce:transition-none">
                    {label}
                  </span>
                  <span className="font-semibold tabular-nums text-white">
                    {number}
                  </span>
                </a>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
